import { useEffect, useMemo, useRef } from 'react'
import { collectionGroup, onSnapshot, query, where } from 'firebase/firestore'
import { useQueryClient } from '@tanstack/react-query'
import { db } from '../../../libs/Firebase'
import { listenWithFreshClaims } from '../../../apiLayer/handlers/directDbHandler'
import { CollectionGroups } from '../../../../../pure-js/libs/Collections'
import { FACILITATOR_ACTIVITY_QUERY_KEYS } from '../../shared/constants/activitySync'
import { useInfiniteTrainingSprints } from '../../../apiLayer/queryHooks/queries'
import { useUser } from '../../../hooks/useUser'

/** Collapse the write storm a live game produces into at most one refetch round per window. */
const INVALIDATE_DEBOUNCE_MS = 2_000 // 2 s

/**
 * Invalidates the activity caches whenever a session document the facilitator manages changes.
 * Mounted once in FacilitatorDashboard, so it reuses the sprint list's first page rather than fetching.
 */
export const useFacilitatorActivitiesRealtimeSync = (): void => {
  const queryClient = useQueryClient()
  const { data: sprintsResponse } = useInfiniteTrainingSprints()
  const { data: user } = useUser()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // A collectionGroup listener fails wholesale on any doc the rules deny, and rules permit session reads only for sprints you facilitate, so subscribe to facilitated sprints only.
  const sprintIds = useMemo<string[]>(() => {
    const uid = user?.id ?? ''
    return (sprintsResponse?.pages?.[0]?.data?.activeSprints ?? [])
      .filter((s) => (s.facilitatedByUserIds ?? []).includes(uid))
      .map((s) => s.id)
      .filter(Boolean)
  }, [sprintsResponse, user?.id])

  useEffect(() => {
    // Firestore 'in' query requires a non-empty array
    if (sprintIds.length === 0) return

    // Firestore 'in' supports max 30 items; chunk if needed
    const IDS_PER_QUERY = 30
    const chunks: string[][] = []
    for (let i = 0; i < sprintIds.length; i += IDS_PER_QUERY) {
      chunks.push(sprintIds.slice(i, i + IDS_PER_QUERY))
    }

    const invalidateAll = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        FACILITATOR_ACTIVITY_QUERY_KEYS.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: [key] })
        })
      }, INVALIDATE_DEBOUNCE_MS)
    }

    // A sprint launched after sign-in is missing from the token until refreshed; the wrapper retries a denied read on a fresh one.
    const stopListening = listenWithFreshClaims(
      (onListenError) => {
        const unsubscribers = chunks.map((chunk) => {
          const sessionsQuery = query(collectionGroup(db, CollectionGroups.SESSIONS), where('sprintId', 'in', chunk))
          return onSnapshot(sessionsQuery, invalidateAll, onListenError)
        })
        return () => unsubscribers.forEach((unsub) => unsub())
      },
      (error) => console.error('facilitator sessions listener failed', error)
    )

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      stopListening()
    }
  }, [sprintIds, queryClient])
}
